/**
 * Supplies the draft editor's Comment action to the floating SelectionToolbar,
 * and owns the composer that opens when it is chosen.
 *
 * No TipTap bubble-menu extension required; we react to ProseMirror selection
 * updates and position off `editor.view.coordsAtPos()` (see DraftEditor).
 * SelectionToolbar owns the floating behaviour; this file owns comment state.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { MessageSquare } from 'lucide-react'
import { useCommentsStore } from '@/stores/comments-store'
import { CommentComposer } from '@/components/drafting/CommentComposer'
import { SelectionToolbar } from '@/components/drafting/SelectionToolbar'
import type { BidMemberInfo } from '@/components/ui/owner-assignment'
import type { CommentAnchor } from '@/lib/comments-api'

const COMPOSER_WIDTH = 360
const COMPOSER_EST_HEIGHT = 190

interface CommentBubbleButtonProps {
  bidId: string
  artifactId: string
  /** Called to resolve the current selection's sectionId + PM positions. */
  resolveSelection: () => {
    sectionId: string | null
    startPos: number | null
    endPos: number | null
    quotedText: string | null
  } | null
  /** Whether the current selection is cross-segment (disables the button). */
  isCrossSegment: () => boolean
  /** Returns the bounding rect of the current PM selection, or null if
   *  collapsed/unavailable. Used instead of window.getSelection() because
   *  PM is the source of truth for the editor's selection. */
  getSelectionRect: () => DOMRect | null
  /** Subscribe to PM selection updates; returns unsubscribe fn. */
  onSelectionUpdate: (cb: () => void) => () => void
  /** Bid members offered as @mention suggestions in the composer. */
  bidMembers?: BidMemberInfo[] | undefined
}

export function CommentBubbleButton({
  bidId,
  artifactId,
  resolveSelection,
  isCrossSegment,
  getSelectionRect,
  onSelectionUpdate,
  bidMembers,
}: CommentBubbleButtonProps) {
  const [bubblePos, setBubblePos] = useState<{ x: number; y: number } | null>(null)
  const [composerOpen, setComposerOpen] = useState(false)
  const [composerAnchor, setComposerAnchor] = useState<CommentAnchor | null>(null)
  const [composerPos, setComposerPos] = useState<{ x: number; y: number } | null>(null)
  const loadPermissions = useCommentsStore((s) => s.loadPermissions)
  const permissionsMap = useCommentsStore((s) => s.permissions)
  const lastSectionIdRef = useRef<string | null>(null)

  // Pre-load artifact-level permissions on mount so the bubble doesn't flicker
  // between "selection made" and "first section permissions arrived". For
  // owners/writers this pre-load gives a correct optimistic answer we can
  // use as a fallback while section-level perms are in flight.
  useEffect(() => {
    if (!bidId || !artifactId) return
    void loadPermissions(bidId, artifactId, null)
  }, [bidId, artifactId, loadPermissions])

  useEffect(() => {
    function updateBubble() {
      if (composerOpen) return

      if (isCrossSegment()) {
        setBubblePos(null)
        return
      }

      // PM is the source of truth for the editor's selection. resolveSelection
      // returns null when the selection is collapsed or outside a section.
      const anchor = resolveSelection()
      if (!anchor || anchor.sectionId == null) {
        setBubblePos(null)
        return
      }

      // Fire-and-forget permission load for the selected section. Falls back
      // to artifact-level perms until this resolves (see below).
      if (lastSectionIdRef.current !== anchor.sectionId) {
        lastSectionIdRef.current = anchor.sectionId
        void loadPermissions(bidId, artifactId, anchor.sectionId)
      }

      const rect = getSelectionRect()
      if (!rect || (rect.width === 0 && rect.height === 0)) {
        setBubblePos(null)
        return
      }
      setBubblePos({
        x: rect.left + rect.width / 2,
        y: rect.top - 8,
      })
    }

    // PM selection updates (via drag, keyboard, programmatic) are the primary
    // trigger. We also listen to scroll so the bubble tracks the selection's
    // rect as the user scrolls a long document.
    const unsubscribe = onSelectionUpdate(updateBubble)
    const onScroll = () => updateBubble()
    document.addEventListener('scroll', onScroll, true)
    // One initial run in case a selection was already present when we mounted.
    updateBubble()
    return () => {
      unsubscribe()
      document.removeEventListener('scroll', onScroll, true)
    }
  }, [
    bidId,
    artifactId,
    loadPermissions,
    resolveSelection,
    isCrossSegment,
    getSelectionRect,
    onSelectionUpdate,
    composerOpen,
  ])

  const permsKey = useMemo(() => {
    if (!lastSectionIdRef.current) return null
    return `${artifactId}:${lastSectionIdRef.current}`
  }, [artifactId, bubblePos])

  // Fall back to artifact-level perms when section-level perms are still
  // loading. For owners/writers the artifact-level answer is correct for all
  // sections of a draft artifact; non-owners get a deny-all fallback via the
  // store. Backend still enforces the real section-level permission on create.
  const artifactPermsKey = `${artifactId}:__artifact__`
  const sectionPerm = permsKey ? permissionsMap[permsKey] : undefined
  const artifactPerm = permissionsMap[artifactPermsKey]
  const canCreate =
    sectionPerm?.canCreate ?? artifactPerm?.canCreate ?? false

  const closeComposer = () => {
    setComposerOpen(false)
    setComposerAnchor(null)
    setComposerPos(null)
  }

  // Single source for the overlay. Opening the composer nulls `bubblePos`, so
  // the early-return branch below is the path actually taken once it is open —
  // rendering it twice previously dropped `bidMembers` there and killed the
  // @mention picker in the anchored composer.
  const composer =
    composerOpen && composerAnchor && composerPos ? (
      <ComposerOverlay
        pos={composerPos}
        anchor={composerAnchor}
        bidId={bidId}
        artifactId={artifactId}
        bidMembers={bidMembers}
        onClose={closeComposer}
      />
    ) : null

  if (!bubblePos || !canCreate) {
    return composer
  }

  // SelectionToolbar preserves the text selection through mousedown, so the
  // anchor is still readable here.
  const openComposer = () => {
    const anchor = resolveSelection()
    if (!anchor) return
    const rect = getSelectionRect()
    setComposerPos(
      rect ? { x: rect.left, y: rect.bottom + 8 } : { x: bubblePos.x, y: bubblePos.y },
    )
    setComposerAnchor({
      sectionId: anchor.sectionId,
      startPos: anchor.startPos,
      endPos: anchor.endPos,
      quotedText: anchor.quotedText,
    })
    setComposerOpen(true)
    setBubblePos(null)
  }

  return (
    <>
      <SelectionToolbar
        pos={bubblePos}
        actions={[
          {
            id: 'comment',
            label: 'Comment',
            icon: MessageSquare,
            onSelect: openComposer,
          },
        ]}
      />
      {composer}
    </>
  )
}

function ComposerOverlay({
  pos,
  anchor,
  bidId,
  artifactId,
  bidMembers,
  onClose,
}: {
  pos: { x: number; y: number }
  anchor: CommentAnchor
  bidId: string
  artifactId: string
  bidMembers?: BidMemberInfo[] | undefined
  onClose: () => void
}) {
  // Clamp inside the viewport so the composer (and its Save button) is always
  // reachable, even when the selection sits near the bottom/right edge.
  const left = Math.max(8, Math.min(pos.x, window.innerWidth - COMPOSER_WIDTH - 8))
  const top = Math.max(8, Math.min(pos.y, window.innerHeight - COMPOSER_EST_HEIGHT - 8))

  return createPortal(
    <div
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 60,
        maxWidth: COMPOSER_WIDTH,
      }}
      className="w-[340px] rounded-lg border border-border bg-card shadow-lg p-3"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <CommentComposer
        bidId={bidId}
        targetId={artifactId}
        anchor={anchor}
        bidMembers={bidMembers}
        onClose={onClose}
      />
    </div>,
    document.body,
  )
}
